import { useState } from "react";
import { Handshake as HandshakeIcon } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useHandshakes } from "@/hooks/useHandshakes";
import type { Handshake, HandshakeStatus } from "@/services/handshakeService";
import {
  fmtMoney,
  statusBadgeVariant,
  handshakeStatusIcon,
  EmptyState,
} from "./helpers";



// ─── Handshakes list ──────────────────────────────────────────────

export function HandshakesList({ onOpen }: { onOpen: (id: string) => void }) {
  const [filterStatus, setFilterStatus] = useState<HandshakeStatus | undefined>(
    undefined,
  );
  const { data, isLoading } = useHandshakes({ status: filterStatus });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  const handshakes = data?.handshakes ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground">Filter:</span>
        {(
          [
            undefined,
            "requested",
            "acknowledged",
            "confirmed",
            "closed",
            "rejected",
            "cancelled",
          ] as const
        ).map((s) => (
          <Button
            key={s ?? "all"}
            variant={filterStatus === s ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterStatus(s)}
          >
            {s ?? "All"}
          </Button>
        ))}
      </div>

      {handshakes.length === 0 ? (
        <EmptyState
          icon={
            <HandshakeIcon className="h-10 w-10 text-muted-foreground/60" />
          }
          title="No handshakes yet"
          description="Send a request to a connected vendor or receive one to get started."
        />
      ) : (
        <div className="space-y-3">
          {handshakes.map((h) => (
            <HandshakeRow key={h._id} handshake={h} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

function HandshakeRow({
  handshake,
  onOpen,
}: {
  handshake: Handshake;
  onOpen: (id: string) => void;
}) {
  return (
    <Card
      className="p-4 hover:bg-accent/30 cursor-pointer transition-colors"
      onClick={() => onOpen(handshake._id)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-mono text-sm">{handshake.handshakeId}</h3>
            <Badge
              variant={statusBadgeVariant(handshake.status)}
              className="gap-1"
            >
              {handshakeStatusIcon(handshake.status)}
              {handshake.status}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {handshake.myRole === "distributor" ? "Outgoing" : "Incoming"}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {handshake.lines.length} line(s) ·{" "}
            <strong className="font-medium">
              {fmtMoney(handshake.grandTotalMinor, handshake.currency)}
            </strong>
            {handshake.delegatedToCashierId && (
              <>
                {" · "}
                <span className="text-xs">Delegated</span>
              </>
            )}
          </p>
        </div>
        <div className="text-xs text-muted-foreground text-right shrink-0">
          {formatDistanceToNow(new Date(handshake.createdAt), {
            addSuffix: true,
          })}
        </div>
      </div>
    </Card>
  );
}
